import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Bell,
  BellOff,
  Bot,
  Send,
  Save,
  Plus,
  Trash2,
  ExternalLink,
  Loader2,
  Check,
  X,
  History,
  ShieldCheck,
} from "lucide-react";

// Admin-facing manager for Telegram push notifications. All bot creds
// live in Mongo (admin_alert_settings singleton) — never in env.
export default function TelegramAlertsManager() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Local editable state
  const [enabled, setEnabled] = useState(false);
  const [botTokenInput, setBotTokenInput] = useState("");
  const [chatIds, setChatIds] = useState([]);
  const [newChatId, setNewChatId] = useState("");
  const [events, setEvents] = useState({ lpg_upload: true, file_attachment: true, forward_file: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/telegram-settings");
      setSettings(data);
      setEnabled(!!data.enabled);
      setChatIds(data.chat_ids || []);
      setEvents(data.events || { lpg_upload: true, file_attachment: true, forward_file: false });
      setBotTokenInput(""); // never prefill the real token
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load Telegram settings");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/telegram-settings/history");
      setHistory(data.rows || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    load();
    loadHistory();
  }, []);

  const isDirty = useMemo(() => {
    if (!settings) return false;
    if (enabled !== !!settings.enabled) return true;
    if (botTokenInput.trim()) return true;
    // Typed-but-not-yet-added chat ID also counts as dirty — Save will pick it up.
    if ((newChatId || "").trim() && !chatIds.includes((newChatId || "").trim())) return true;
    const a = JSON.stringify(chatIds || []);
    const b = JSON.stringify(settings.chat_ids || []);
    if (a !== b) return true;
    const ea = JSON.stringify(events || {});
    const eb = JSON.stringify(settings.events || {});
    if (ea !== eb) return true;
    return false;
  }, [settings, enabled, botTokenInput, chatIds, events, newChatId]);

  const addChatId = () => {
    const v = (newChatId || "").trim();
    if (!v) return;
    if (chatIds.includes(v)) {
      toast.warning("Chat ID already added");
      return;
    }
    setChatIds([...chatIds, v]);
    setNewChatId("");
  };

  const removeChatId = (id) => setChatIds(chatIds.filter((x) => x !== id));

  // Success banner state — brighter/louder confirmation than a toast alone.
  const [saveBanner, setSaveBanner] = useState("");

  const save = async () => {
    // Auto-add: if the user typed a chat ID but forgot to click "+ Add",
    // merge it into the list before saving so nothing is lost silently.
    let finalChatIds = chatIds;
    const pending = (newChatId || "").trim();
    if (pending && !chatIds.includes(pending)) {
      finalChatIds = [...chatIds, pending];
    }
    // Guard: if we're about to save with 0 chat IDs AND the master switch is ON,
    // the admin will be confused later ("kyu msg nahi aya?"). Warn + confirm.
    if (enabled && finalChatIds.length === 0) {
      const ok = window.confirm(
        "⚠️ Chat IDs list is empty.\n\n" +
        "Alerts will NOT be sent to Telegram until you add at least one Chat ID.\n\n" +
        "Save anyway?"
      );
      if (!ok) {
        setSaving(false);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        enabled,
        chat_ids: finalChatIds,
        events,
      };
      const tok = botTokenInput.trim();
      if (tok) payload.bot_token = tok;
      const { data } = await api.put("/admin/telegram-settings", payload);
      setSettings(data);
      setBotTokenInput("");
      setChatIds(data.chat_ids || []);
      setNewChatId(""); // consume the pending input
      const parts = [];
      if (data.bot_token_set) parts.push("Bot token active");
      if ((data.chat_ids || []).length > 0)
        parts.push(`${data.chat_ids.length} chat ID${data.chat_ids.length > 1 ? "s" : ""} configured`);
      else parts.push("no chat IDs yet");
      const bannerMsg = `Saved · ${parts.join(" · ")}`;
      setSaveBanner(bannerMsg);
      toast.success(bannerMsg);
      // Auto-clear the banner after 6 seconds.
      setTimeout(() => setSaveBanner((m) => (m === bannerMsg ? "" : m)), 6000);
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/telegram-settings/test");
      if (data.ok) {
        toast.success(`Test sent to ${data.results?.length || 0} chat(s) ✓`);
      } else {
        const err = data.error || (data.results || []).find((r) => !r.ok)?.error || "Test failed";
        toast.error(`Test failed: ${err}`);
      }
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center text-[#6B6B61]">
        <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
        Loading Telegram settings…
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 md:px-0">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-[#385C4B]/10 flex items-center justify-center text-[#385C4B]">
          <Bot className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <h1
            className="text-xl font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="telegram-page-title"
          >
            Telegram Admin Alerts
          </h1>
          <p className="text-sm text-[#6B6B61] mt-0.5">
            Real-time push notification whenever a member uploads LPG codes or shares a file.
          </p>
        </div>
      </div>

      {/* Success banner — 6s auto-dismiss */}
      {saveBanner && (
        <div
          className="mb-5 rounded-lg border border-[#385C4B]/40 bg-[#385C4B]/10 text-[#385C4B] px-4 py-3 flex items-center gap-3"
          data-testid="telegram-save-banner"
        >
          <div className="w-8 h-8 rounded-full bg-[#385C4B] text-white flex items-center justify-center flex-shrink-0">
            <Check className="w-4 h-4" strokeWidth={3} />
          </div>
          <div
            className="text-sm font-semibold"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {saveBanner}
          </div>
          <button
            type="button"
            onClick={() => setSaveBanner("")}
            data-testid="telegram-save-banner-close"
            className="ml-auto text-[#385C4B] hover:text-[#1C1C19] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Won't-send warning — persistent, until user adds a chat ID or turns switch off */}
      {enabled && settings?.bot_token_set && (chatIds || []).length === 0 && !newChatId.trim() && (
        <div
          className="mb-5 rounded-lg border border-[#C8835A]/50 bg-[#C8835A]/10 text-[#8A6046] px-4 py-3 flex items-start gap-3"
          data-testid="telegram-empty-chatids-warning"
        >
          <div className="w-8 h-8 rounded-full bg-[#C8835A] text-white flex items-center justify-center flex-shrink-0">
            <BellOff className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="text-sm leading-snug flex-1">
            <div className="font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
              Alerts are OFF — chat IDs missing
            </div>
            <div className="text-xs mt-0.5">
              Aapka bot token save hai aur master switch ON hai, lekin abhi tak koi Chat ID nahi juda. Add karo aur Save karo — tabhi Telegram par msg jaayega.
            </div>
          </div>
        </div>
      )}

      <div
        className={`rounded-lg border p-4 md:p-5 mb-5 transition-colors ${
          enabled ? "bg-[#385C4B]/5 border-[#385C4B]/30" : "bg-white border-[#DCDCD3]"
        }`}
      >
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div
              className="text-sm font-semibold text-[#1C1C19] flex items-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {enabled ? (
                <Bell className="w-4 h-4 text-[#385C4B]" strokeWidth={2} />
              ) : (
                <BellOff className="w-4 h-4 text-[#6B6B61]" strokeWidth={2} />
              )}
              Master switch
            </div>
            <p className="text-xs text-[#6B6B61] mt-1">
              When ON, alerts are dispatched to all configured chat IDs. When OFF, nothing is sent
              even if bot token &amp; chat IDs are set.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEnabled(!enabled)}
            data-testid="telegram-master-toggle"
            aria-pressed={enabled}
            className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${
              enabled ? "bg-[#385C4B]" : "bg-[#DCDCD3]"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                enabled ? "translate-x-7" : "translate-x-0"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Bot token */}
      <div className="rounded-lg border border-[#DCDCD3] bg-white p-4 md:p-5 mb-5">
        <label
          className="text-xs tracking-[0.2em] uppercase text-[#6B6B61] font-mono mb-2 block"
        >
          Bot Token
        </label>
        <div className="flex items-center gap-2 mb-2">
          <input
            type="password"
            value={botTokenInput}
            onChange={(e) => setBotTokenInput(e.target.value)}
            placeholder={
              settings?.bot_token_set
                ? `Current: ${settings.bot_token_masked} — leave blank to keep`
                : "e.g. 7123456789:AAH...  (from @BotFather)"
            }
            data-testid="telegram-bot-token-input"
            className="flex-1 px-3 py-2 border border-[#DCDCD3] rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-[#385C4B]/30 focus:border-[#385C4B]"
            autoComplete="off"
          />
          {settings?.bot_token_set && (
            <span
              className="inline-flex items-center gap-1 text-xs text-[#385C4B] font-mono px-2 py-1 rounded-md bg-[#385C4B]/10"
              data-testid="telegram-token-status"
            >
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} /> Set
            </span>
          )}
        </div>
        <a
          href="https://t.me/BotFather"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-[#385C4B] hover:underline"
        >
          Get a token from @BotFather <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Chat IDs */}
      <div className="rounded-lg border border-[#DCDCD3] bg-white p-4 md:p-5 mb-5">
        <label className="text-xs tracking-[0.2em] uppercase text-[#6B6B61] font-mono mb-2 block">
          Chat IDs
        </label>
        <p className="text-xs text-[#6B6B61] mb-3">
          Each ID gets its own copy of every alert. Use a personal chat_id (positive number) for
          one admin, or a group chat_id (starts with <code>-100</code>) so every admin in that
          Telegram group is notified via a single ID.
        </p>
        {chatIds.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="telegram-chatids-list">
            {chatIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#385C4B]/10 text-[#385C4B] rounded-md text-xs font-mono"
                data-testid={`telegram-chatid-chip-${id}`}
              >
                {id}
                <button
                  type="button"
                  onClick={() => removeChatId(id)}
                  data-testid={`telegram-chatid-remove-${id}`}
                  className="hover:text-[#C8553D] transition-colors"
                  title="Remove"
                >
                  <X className="w-3 h-3" strokeWidth={2.5} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={newChatId}
            onChange={(e) => setNewChatId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addChatId();
              }
            }}
            placeholder="e.g. -1001234567890 or 987654321"
            data-testid="telegram-chatid-input"
            className="flex-1 px-3 py-2 border border-[#DCDCD3] rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-[#385C4B]/30 focus:border-[#385C4B] font-mono"
          />
          <button
            type="button"
            onClick={addChatId}
            data-testid="telegram-chatid-add"
            className="px-3 py-2 bg-[#385C4B] text-white rounded-md text-sm hover:bg-[#2D4A3C] transition-colors flex items-center gap-1"
          >
            <Plus className="w-4 h-4" strokeWidth={2} /> Add
          </button>
        </div>
        <a
          href="https://t.me/userinfobot"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-[#385C4B] hover:underline mt-2"
        >
          Get your chat ID via @userinfobot <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Per-event toggles */}
      <div className="rounded-lg border border-[#DCDCD3] bg-white p-4 md:p-5 mb-5">
        <label className="text-xs tracking-[0.2em] uppercase text-[#6B6B61] font-mono mb-3 block">
          Alert events
        </label>
        <div className="space-y-2">
          {[
            {
              key: "lpg_upload",
              label: "LPG uploads",
              desc: "Fires when a member sends LPG codes (chat or bulk paste).",
            },
            {
              key: "file_attachment",
              label: "File attachments (text notification)",
              desc: "Fires when a member shares an image / PDF / Excel / CSV file.",
            },
            {
              key: "forward_file",
              label: "Forward actual file to Telegram",
              desc: "Uploads the file itself so admins can download from Telegram (≤45 MB · larger files auto-skip). Turn ON only if bandwidth/data isn't a concern.",
            },
          ].map((ev) => (
            <label
              key={ev.key}
              className="flex items-start gap-3 p-2.5 rounded-md hover:bg-[#F5F5F0]/60 cursor-pointer transition-colors"
              data-testid={`telegram-event-row-${ev.key}`}
            >
              <input
                type="checkbox"
                checked={!!events[ev.key]}
                onChange={(e) => setEvents({ ...events, [ev.key]: e.target.checked })}
                data-testid={`telegram-event-${ev.key}`}
                className="mt-1 w-4 h-4 accent-[#385C4B]"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1C1C19]">{ev.label}</div>
                <div className="text-xs text-[#6B6B61]">{ev.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Action row */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          type="button"
          onClick={save}
          disabled={saving || !isDirty}
          data-testid="telegram-save-btn"
          className="px-4 py-2 bg-[#385C4B] text-white rounded-md text-sm hover:bg-[#2D4A3C] transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
          ) : (
            <Save className="w-4 h-4" strokeWidth={2} />
          )}
          Save settings
        </button>
        <button
          type="button"
          onClick={sendTest}
          disabled={testing || !settings?.bot_token_set || (chatIds || []).length === 0}
          data-testid="telegram-test-send"
          title={
            !settings?.bot_token_set
              ? "Save a bot token first"
              : (chatIds || []).length === 0
              ? "Add at least one chat ID"
              : "Send a test message to every chat ID"
          }
          className="px-4 py-2 border border-[#385C4B] text-[#385C4B] rounded-md text-sm hover:bg-[#385C4B]/10 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {testing ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
          ) : (
            <Send className="w-4 h-4" strokeWidth={2} />
          )}
          Send test message
        </button>
        <button
          type="button"
          onClick={() => {
            setHistoryOpen(!historyOpen);
            if (!historyOpen) loadHistory();
          }}
          data-testid="telegram-history-toggle"
          className="px-4 py-2 border border-[#DCDCD3] text-[#6B6B61] rounded-md text-sm hover:bg-[#F5F5F0] transition-colors flex items-center gap-2 ml-auto"
        >
          <History className="w-4 h-4" strokeWidth={2} />
          {historyOpen ? "Hide" : "Show"} history
        </button>
      </div>

      {/* History */}
      {historyOpen && (
        <div
          className="rounded-lg border border-[#DCDCD3] bg-white overflow-hidden mb-6"
          data-testid="telegram-history-panel"
        >
          <div className="px-4 py-2.5 bg-[#F5F5F0] border-b border-[#DCDCD3] text-xs font-mono uppercase tracking-wider text-[#6B6B61]">
            Recent alerts · {history.length}
          </div>
          {history.length === 0 ? (
            <div className="px-4 py-6 text-sm text-center text-[#6B6B61] italic">
              No alerts sent yet.
            </div>
          ) : (
            <div className="divide-y divide-[#DCDCD3]/60 max-h-80 overflow-y-auto">
              {history.map((row) => (
                <div
                  key={row.id}
                  className="px-4 py-2.5 flex items-start gap-3 text-xs"
                  data-testid={`telegram-history-row-${row.id}`}
                >
                  {row.failure_count === 0 ? (
                    <Check className="w-3.5 h-3.5 text-[#385C4B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  ) : (
                    <X className="w-3.5 h-3.5 text-[#C8553D] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-mono uppercase text-[10px] text-[#385C4B] tracking-wider">
                        {row.event_type}
                      </span>
                      <span className="font-mono text-[10px] text-[#6B6B61]">
                        {new Date(row.sent_at).toLocaleString()}
                      </span>
                      <span className="text-[10px] text-[#6B6B61]">
                        {row.success_count}/{row.chat_id_count} delivered
                      </span>
                    </div>
                    <div
                      className="mt-0.5 text-[#1C1C19] truncate"
                      dangerouslySetInnerHTML={{ __html: row.text_preview || "" }}
                    />
                    {row.sample_error && (
                      <div className="mt-0.5 text-[#C8553D]">↳ {row.sample_error}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Help footer */}
      <div className="rounded-lg bg-[#F5F5F0] border border-[#DCDCD3] p-4 text-xs text-[#6B6B61] leading-relaxed">
        <div
          className="text-sm font-semibold text-[#1C1C19] mb-2"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Setup in 3 steps
        </div>
        <ol className="list-decimal pl-5 space-y-1">
          <li>
            Message <b>@BotFather</b> on Telegram → <code>/newbot</code> → copy the bot token here.
          </li>
          <li>
            Send any message to your new bot (so it can reach you). Then message{" "}
            <b>@userinfobot</b> and paste the Chat ID above. For a group, add the bot + @userinfobot
            to the group and use the group Chat ID (starts with <code>-100</code>).
          </li>
          <li>
            Turn the master switch ON and hit <b>Send test message</b> — you should see the test
            arrive on Telegram within a second.
          </li>
        </ol>
      </div>
    </div>
  );
}
